import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import router from '@/router';
import { useNotifications } from '@/composables/useNotifications';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const token = ref(localStorage.getItem('id_token') || null);
  const needsConsent = ref(false);
  const showUnauthorizedDomainForm = ref(false);
  const { showError } = useNotifications();

  const isAuthenticated = computed(() => !!token.value);
  const isAdmin = computed(() => user.value?.role?.role_name === 'toolbox_super_admin');

  function hideUnauthorizedDomainForm() {
    showUnauthorizedDomainForm.value = false;
  }

  async function handleAuthResponse(response) {
    if (response.status === 202 && response.data.needs_consent) {
      needsConsent.value = true;
    } else {
      user.value = response.data;
      needsConsent.value = false;
      
      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl) {
          localStorage.removeItem('returnUrl');
          router.push(returnUrl);
      } else if (router.currentRoute.value.path === '/login') {
        router.push('/');
      }
    }
  }

  async function login(googleToken) {
    localStorage.setItem('id_token', googleToken);
    token.value = googleToken;
    try {
      const response = await api.getMe();
      await handleAuthResponse(response);
    } catch (error) {
      if (error.response && error.response.status === 202) {
        await handleAuthResponse(error.response);
      } else {
        const errorMessage = error.response?.data?.message || "An unknown error occurred during login.";
        if (error.response?.data?.error_code === "DOMAIN_NOT_AUTHORIZED") {
          showUnauthorizedDomainForm.value = true;
        }
        showError(errorMessage);
        logout();
      }
    }
  }

  function logout() {
    localStorage.removeItem('id_token');
    token.value = null;
    user.value = null;
    needsConsent.value = false;
    router.push('/login');
  }

  async function tryAutoLogin() {
    if (token.value) {
      try {
        const response = await api.getMe();
        await handleAuthResponse(response);
      } catch (error) {
        if (error.response && error.response.status === 202) {
          await handleAuthResponse(error.response);
        } else {
          const errorMessage = error.response?.data?.message || "Your session could not be restored.";
          showError(errorMessage);
          logout();
        }
      }
    }
  }

  async function createUser() {
    try {
      const response = await api.createUser();
      user.value = response.data;
      needsConsent.value = false;
      router.push('/');
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unknown error occurred during user creation.";
      showError(errorMessage);
      logout();
    }
  }

  return { 
    user, 
    token, 
    isAuthenticated, 
    isAdmin, 
    needsConsent, 
    showUnauthorizedDomainForm,
    login, 
    logout, 
    tryAutoLogin, 
    createUser,
    hideUnauthorizedDomainForm
  };
});
